import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";

const yn = (v) => (String(v).toUpperCase() === "Y" || v === true ? "Y" : "N");

const VariantStockVisibilityModal = ({
  show,
  onHide,
  item,        // full item (with prices[])
  baseUrl,
  config,
  onUpdated,   // refresh catalogue callback
}) => {
  const rows = useMemo(() => (Array.isArray(item?.prices) ? item.prices : []), [item]);

  // map: { [priceRowId]: { isVisible: "Y|N", isStock: "Y|N" } }
  const [stateMap, setStateMap] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!show || !item) return;

    const init = {};
    for (const p of rows) {
      init[String(p.id)] = {
        isVisible: yn(p.visibility),
        isStock: yn(p.inStock),
      };
    }
    setStateMap(init);
  }, [show, item, rows]);

  const onToggle = (priceRowId, key) => {
    const id = String(priceRowId);
    setStateMap((prev) => {
      const cur = prev?.[id] || { isVisible: "N", isStock: "N" };
      return {
        ...(prev || {}),
        [id]: {
          ...cur,
          [key]: cur[key] === "Y" ? "N" : "Y",
        },
      };
    });
  };

  const saveRow = async (priceRowId) => {
    const id = String(priceRowId);
    const st = stateMap[id] || {};

    const payload = {
      id,
      isVisible: st.isVisible || "N",
      isStock: st.isStock || "N",
    };

    setSavingId(id);
    try {
      const res = await axios.post(
        `${baseUrl}/api/Order/updateStocksAvailabilty`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        Swal.fire("Success", res.data.responseMessage || "Updated", "success");
        onUpdated && onUpdated();
      } else {
        Swal.fire("Error", res?.data?.responseMessage || "Failed", "error");
      }
    } catch (e) {
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setSavingId(null);
    }
  };

  if (!item) return null;

  return (
    <Modal show={!!show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          Variants Status — {item.item_name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Item-level status (optional display) */}
        <div className="border rounded p-2 mb-3 bg-light d-flex justify-content-between flex-wrap">
          <div className="small">
            <b>Item Visible:</b>{" "}
            <span className={yn(item.visibility) === "Y" ? "text-success" : "text-danger"}>
              {yn(item.visibility)}
            </span>
          </div>
          <div className="small">
            <b>Item Stock:</b>{" "}
            <span className={yn(item.inStock) === "Y" ? "text-success" : "text-danger"}>
              {yn(item.inStock)}
            </span>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-muted">No variants found.</div>
        ) : (
          rows.map((p) => {
            const pid = String(p.id);
            const st = stateMap[pid] || { isVisible: "N", isStock: "N" };

            return (
              <div key={pid} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  {/* Variant Info */}
                  <div>
                    <div className="fw-semibold">
                      {p.variantName || p.size || "Variant"} — ₹{p.price || "-"}
                    </div>
                    <div className="small text-muted">
                      Row ID: {pid} {p.variantId ? `• Variant ID: ${p.variantId}` : ""}
                    </div>
                  </div>

                  {/* Current Status badges */}
                  <div className="d-flex gap-2">
                    <span
                      className={`badge ${st.isVisible === "Y" ? "bg-success" : "bg-danger"}`}
                    >
                      Visible: {st.isVisible}
                    </span>
                    <span
                      className={`badge ${st.isStock === "Y" ? "bg-primary" : "bg-warning text-dark"}`}
                    >
                      Stock: {st.isStock === "Y" ? "IN" : "OUT"}
                    </span>
                  </div>
                </div>

                {/* Switch controls */}
                <div className="d-flex align-items-center gap-4 flex-wrap mt-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`vis_${pid}`}
                      checked={st.isVisible === "Y"}
                      onChange={() => onToggle(pid, "isVisible")}
                    />
                    <label className="form-check-label" htmlFor={`vis_${pid}`}>
                      Visibility
                    </label>
                  </div>

                  {/* <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`stock_${pid}`}
                      checked={st.isStock === "Y"}
                      onChange={() => onToggle(pid, "isStock")}
                    />
                    <label className="form-check-label" htmlFor={`stock_${pid}`}>
                      In Stock
                    </label>
                  </div> */}

                  <button
                    className="btn btn-sm btn-primary ms-auto"
                    onClick={() => saveRow(pid)}
                    disabled={savingId === pid}
                  >
                    {savingId === pid ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default VariantStockVisibilityModal;
